import type { decode } from "@gandlaf21/bolt11-decode";
import { parse } from "tldts";
import { Bob } from "../stores/Bob";
import { BobPayattemptFromServer } from "../stores/PayAttempt";
import { BOB_MODALS } from "./constants";

export type LightningInputKind =
  | "possible-lightning-address"
  | "address-shaped-invalid-tld"
  | "possible-lnurl"
  | "possible-lightning-invoice"
  | false;

// TLDs that are technically valid (ICANN) but we reject because they're
// near-universally typos of a real suffix. `.co` → almost always `.com`.
const BLACKLISTED_TLDS = new Set(["co"]);

export const classifyLightningInput = (raw: string): LightningInputKind => {
  const str = (raw ?? "").trim().toLowerCase();

  // ── 1. Email-style Lightning address ───────────────────────────────
  // Shape check first, then confirm the domain ends in a real public suffix
  // (Public Suffix List via `tldts`) so obvious typos like `exchange.ap`
  // don't trigger a backend lookup. The backend remains the source of truth
  // for whether a real domain actually hosts a Lightning address.
  const addressRegex = /^[a-z0-9._-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
  if (addressRegex.test(str)) {
    const domain = str.slice(str.indexOf("@") + 1);
    const { isIcann, publicSuffix } = parse(domain);
    // `isIcann` is only true when the TLD is an actual ICANN public suffix.
    // tldts otherwise guesses the last label as a suffix, so checking
    // `publicSuffix` alone would still accept typos like `.ap` / `.con`.
    if (isIcann && !BLACKLISTED_TLDS.has(publicSuffix ?? "")) {
      return "possible-lightning-address";
    }
    // Email-shaped but the TLD isn't real (e.g. `exchange.ap`). Flag it
    // distinctly so the UI can say so instead of falling through to the
    // generic invoice error.
    return "address-shaped-invalid-tld";
  }

  // ── 2. LNURL (Bech32 HRP “lnurl1…”) ────────────────────────────────
  // • HRP must be lnurl + separator “1”; Bech32 data is every lowercase letter
  //   and digit except b, i, o and 1. ≥10 chars ≈ ≥50 bits
  const lnurlRegex = /^lnurl1[ac-hj-np-z02-9]{10,}$/; // no ‘@’, case-insensitive via str.toLowerCase()
  if (lnurlRegex.test(str)) {
    return "possible-lnurl";
  }

  // ── 3. BOLT-11 Lightning invoice (lenient) ─────────────────────────
  const invoiceRegex = /^ln[a-z0-9]{18,}$/; // “ln” + ≥18 bech32 chars
  const bech32Charset = /^[a-z0-9]+$/;
  if (invoiceRegex.test(str) && bech32Charset.test(str)) {
    return "possible-lightning-invoice";
  }

  // ── 4. Not Lightning-related ───────────────────────────────────────
  return false;
};

/** The fields your UI / store actually cares about */
export interface InvoiceSummary {
  /** Satoshis the invoice requests, or `null` when it’s “amountless”            */
  amountInSatoshis: number | null;
  /** Description string from tag `d`, or `null` if the invoice has none         */
  description: string | null;
  /** Absolute UNIX time (seconds) when the invoice becomes invalid (expiry)     */
  expiresAt: number; // always present – BOLT-11 defaults to +3600 s
}

/**
 * Convert the raw object returned by `decode()` into an `InvoiceSummary`.
 *
 * @param decoded — result of `decode(invoiceStr)`
 * @returns a shape that’s easy to feed into MobX-Keystone, React props, etc.
 */
export function summariseBolt11Invoice(
  decoded: ReturnType<typeof decode>
): InvoiceSummary {
  // ---- locate helpful sections --------------------------------------------
  console.log("in summariseBolt11Invoice with decoded:", decoded);
  const sections = decoded.sections;

  const amountSec = sections.find((s) => s.name === "amount");
  const descSec = sections.find((s) => s.name === "description");
  console.log("Description section:", descSec);
  const tsSec = sections.find((s) => s.name === "timestamp");
  const expSec = sections.find((s) => s.name === "expiry"); // tag “x”

  // ---- amount --------------------------------------------------------------
  // • Library stores millisatoshis as *string*; divide by 1 000 → satoshis.
  const amountInSatoshis =
    amountSec && amountSec.value != null
      ? Number(amountSec.value) / 1_000
      : null; // “amountless” = user chooses amount

  // ---- description ---------------------------------------------------------
  // The `value` from the decode library can be a string or a Buffer/Uint8Array.
  // The original `typeof ... === "string"` check was too strict.
  // This now uses logic from another part of the app that correctly handles the value.
  const description =
    descSec?.value && (descSec.value as any).length > 0
      ? String(descSec.value)
      : null;
  console.log("Description:", description);
  // ---- expiry --------------------------------------------------------------
  // BOLT-11 default expiry is 3 600 s (one hour) if tag “x” is missing.
  const timestamp = tsSec ? Number(tsSec.value) : Math.floor(Date.now() / 1000);
  const expiry = expSec ? Number(expSec.value) : 3_600;

  const expiresAt = timestamp + expiry;

  return { amountInSatoshis, description, expiresAt };
}

export const uint8ArrayToHex = (bytes: Uint8Array): string =>
  Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");

export const handleFirstPayattemptFromServer = (
  bobPayAttemptFromServer: BobPayattemptFromServer,
  bob: Bob
): void => {
  console.log(
    "Handling first from server pay attempt:",
    bobPayAttemptFromServer
  );
  if (
    bobPayAttemptFromServer.attempt_status === "has_failed" &&
    bob?.selectedPayAttempt?.uuid == bobPayAttemptFromServer.uuid
  ) {
    bob.setOpenModal(BOB_MODALS.PAY_ATTEMPT_FAILED);
    bob.setOpenPage(null);
  }

  if (bobPayAttemptFromServer.attempt_status !== "has_succeeded") {
    console.log("First pay attempt is not a success, return here");
    return;
  }
  const eventAlreadyMadeFromThisSuccess = bob.workspace.events.find((event) => {
    return (
      event.kind === "payattempt-succeeded" &&
      event.uuid === bobPayAttemptFromServer.uuid
    );
  });
  if (eventAlreadyMadeFromThisSuccess) {
    console.log("Event already made for this success, return here");
    return;
  }
  bob.workspace.makeEvent(
    "Payment attempt succeeded",
    `A payment attempt in the amount of ${bobPayAttemptFromServer.satoshis_amount.toLocaleString()} satoshis has succeeded.`,
    "payattempt-succeeded",
    bobPayAttemptFromServer.pay_request,
    bobPayAttemptFromServer.uuid
  );

  if (bob?.selectedPayAttempt?.uuid == bobPayAttemptFromServer.uuid) {
    console.log(
      "previously we did bob.setOpenPage(null) here but it was weird because user quickly saw his new balance before success"
    );
    bob.setOpenModal(BOB_MODALS.PAY_ATTEMPT_WAS_SUCCESSFUL);
    bob.setOpenPage(null);
  }
};
